/* Engine-Limits: exakte Auslastung aus einer BSP und grobe Schätzung der Clipnodes aus einer Map. Keine DOM-Abhängigkeit. */
import type { Doc, Vec3 } from '../model/types.ts';
import type { BspFile } from '../formats/bsp-parse.ts';
import Core from '../core.ts';
import I18n from '../i18n.ts';
/** Eine Zeile der Auslastung: benutzt, erlaubt, Verhältnis; soft = Obergrenze nur laut Compiler (Sternchen) */
export interface LimitRow { key: string; label: string; used: number; max: number; ratio: number; soft: boolean }
/** Einordnung der Brushes für die Clipnode-Schätzung */
export interface Classification { ax: number; flat: number; steep: number; iso: number; isoFaces: number; total: number; isoList: { id: string; faces: number }[] }
export interface Estimate { est: number; lo: number; hi: number; max: number; parts: { ax: number; flat: number; steep: number; iso: number } }
export type Level = 'ok' | 'warn' | 'err';
export interface BspLimitRow extends LimitRow { level: Level }

const Limits = (function () {
  'use strict';
  const _ = I18n.t;
  // Grenzen der Original-Engine (Half-Life / Opposing Force). Compiler-Erweiterungen wie bei SDHLT erlauben teils mehr, die Engine nicht.
  const LIM: Record<string, [string, number]> = {
    clip: ['lim.l.clip', 32767], nodes: ['Nodes', 32767], planes: ['Planes', 32767], leafs: ['Leafs', 8192], faces: ['lim.l.faces', 65535], verts: ['Vertices', 65535],
    texinfo: ['Texinfo', 8192], models: ['lim.l.models', 400], marks: ['Marksurfaces', 65535], edges: ['Edges', 256000], surf: ['Surfedges', 512000],
    ent: ['lim.l.ent', 131072], vis: ['lim.l.vis', 2097152], light: ['lim.l.light', 3145728], tex: ['lim.l.tex', 4194304]
  };
  const SIZES: Record<string, number> = { clip: 8, nodes: 24, planes: 20, leafs: 28, faces: 20, verts: 12, texinfo: 40, models: 64, marks: 2, edges: 4, surf: 4 };
  const LUMPIDX: Record<string, number> = { clip: 9, nodes: 5, planes: 1, leafs: 10, faces: 7, verts: 3, texinfo: 6, models: 14, marks: 11, edges: 12, surf: 13, ent: 0, vis: 4, light: 8, tex: 2 };

  /** Auslastung aller Limits aus den Lump-Größen einer geparsten BSP (Bsp.parse). */
  function bspUsage(b: BspFile): LimitRow[] {
    const rows: LimitRow[] = [];
    Object.keys(LIM).forEach((k) => {
      const l = b.lumps[LUMPIDX[k]]; if (!l) return;
      const used = SIZES[k] ? Math.floor(l.len / SIZES[k]) : l.len;
      rows.push({ key: k, label: LIM[k][0].indexOf('lim.l.') === 0 ? _(LIM[k][0]) : LIM[k][0], used: used, max: LIM[k][1], ratio: used / LIM[k][1], soft: _(LIM[k][0]).slice(-1) === '*' });
    });
    return rows.sort((a, c) => c.ratio - a.ratio);
  }

  // Kosten je Brush-Klasse in Clipnodes (aus Messungen mit hlcsg/hlbsp an Testmaps angepasst):
  //  ax = achsparallele Box, flat = Dreiecks-Prisma im Mesh (flache Fläche), steep = Mesh mit steilen/überhängenden Flächen,
  //  iso = jede schräge Fläche an einem freistehenden schrägen Brush (Palmwedel, Dachkeile …) – das ist der teuerste Fall
  const COEF = { ax: 8.4, flat: 7.0, steep: 2.1, iso: 9.7 };
  const BAND = 0.30;

  /** Brushes einordnen (Core.allBrushes-Format). */
  function classify(doc: Doc): Classification {
    const bs = Core.allBrushes(doc), vk = (v: Vec3): string => Math.round(v[0]) + ',' + Math.round(v[1]) + ',' + Math.round(v[2]);
    const cnt = new Map<string, number>();
    bs.forEach((x) => { new Set(x.b.verts.map(vk)).forEach((s) => cnt.set(s, (cnt.get(s) || 0) + 1)); });
    const iso: { id: string; faces: number }[] = [], c: Classification = { ax: 0, flat: 0, steep: 0, iso: 0, isoFaces: 0, total: bs.length, isoList: iso };
    bs.forEach((x) => {
      const b = x.b; let axial = true, fn = 0, maxnz = 0;
      b.faces.forEach((f) => {
        const n = Core.faceNormal(b.verts, f.vi);
        if (Math.max(Math.abs(n[0]), Math.abs(n[1]), Math.abs(n[2])) <= 0.9999) { axial = false; fn++; if (n[2] > 0) maxnz = Math.max(maxnz, n[2]); }
      });
      if (axial) { c.ax++; return; }
      const shared = b.verts.filter((v) => (cnt.get(vk(v)) as number) > 1).length;
      if (shared >= 2) { if (b.faces.length <= 5 && maxnz > 0.75) c.flat++; else c.steep++; }
      else { c.iso++; c.isoFaces += fn; iso.push({ id: b.id, faces: fn }); }
    });
    c.isoList = iso.sort((a, d) => d.faces - a.faces);
    return c;
  }
  function estimate(c: Classification): Estimate {
    const est = Math.round(c.ax * COEF.ax + c.flat * COEF.flat + c.steep * COEF.steep + c.isoFaces * COEF.iso);
    return { est: est, lo: Math.round(est * (1 - BAND)), hi: Math.round(est * (1 + BAND)), max: LIM.clip[1], parts: { ax: Math.round(c.ax * COEF.ax), flat: Math.round(c.flat * COEF.flat), steep: Math.round(c.steep * COEF.steep), iso: Math.round(c.isoFaces * COEF.iso) } };
  }
  /** 'ok' | 'warn' | 'err' nach der Obergrenze der Schätzung. */
  function level(e: Estimate): Level { return e.hi < e.max * 0.8 ? 'ok' : e.est < e.max * 0.85 ? 'warn' : 'err'; }
  function ratioLevel(r: number): Level { return r < 0.75 ? 'ok' : r < 0.92 ? 'warn' : 'err'; }

  /** Zeilen der Auslastung für die Anzeige: die vollsten zuerst; bei „weichen“ Grenzen (Compiler-Erweiterungen) ist Rot nur Gelb */
  function bspRows(b: BspFile, count = 9): BspLimitRow[] {
    return bspUsage(b).slice(0, count).map((r) => { let lv = ratioLevel(r.ratio); if (r.soft && lv === 'err') lv = 'warn'; return Object.assign({}, r, { level: lv }); });
  }
  /** Stufe für die Zahl der Entities (Engine-Grenze etwa 900 Edicts) */
  const edictLevel = (n: number): Level => (n > 800 ? 'err' : n > 600 ? 'warn' : 'ok');

  const api = { LIM, bspUsage, bspRows, classify, estimate, level, ratioLevel, edictLevel, COEF, BAND };
  return api;
})();
export default Limits;
